// Backtest admin view (Phase 4 / B-4). Surfaces the daily
// backtest-runner output: rolling stats by (period × market × action ×
// sentiment_band × quality_verdict × window) and per-suggestion
// drilldown.
//
// Operator workflow:
//   1. Pick a period (7/30/90) at top.
//   2. Scan the rolling stats table — high sample-size + high hit-rate
//      buckets are the LLM's "what's actually working" patterns.
//   3. Drill down into recent buy/sell suggestions to see individual
//      hits / misses against price action.
//   4. Hit "지금 실행" to refresh after enabling overseas snapshots
//      or making a code change that should re-score history.
//
// All semantic admin-design tokens — no raw Tailwind colours.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  type BacktestResultRow,
  type BacktestStatsRow,
} from '@/api/client'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Select,
} from '@/admin-design/_common'
import { formatRelativeKST, formatTime } from '@/lib/utils'
import { symbolName } from '@/lib/krx-names'

type Period = 7 | 30 | 90
type MarketFilter = 'all' | 'KR' | 'US'
type ActionFilter = 'all' | 'buy' | 'sell'
type WindowFilter = 'all' | 1 | 5 | 20

export default function Backtest() {
  const qc = useQueryClient()
  const [period, setPeriod] = useState<Period>(7)
  const [statsMarket, setStatsMarket] = useState<MarketFilter>('all')
  const [resultsMarket, setResultsMarket] = useState<MarketFilter>('all')
  const [resultsAction, setResultsAction] = useState<ActionFilter>('all')
  const [resultsWindow, setResultsWindow] = useState<WindowFilter>('all')

  const stats = useQuery({
    queryKey: ['backtest', 'stats', period, statsMarket],
    queryFn: () =>
      api.backtestStats({
        period_days: period,
        market: statsMarket === 'all' ? undefined : statsMarket,
        limit: 200,
      }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })

  const results = useQuery({
    queryKey: ['backtest', 'results', resultsMarket, resultsAction, resultsWindow],
    queryFn: () =>
      api.backtestResults({
        market: resultsMarket === 'all' ? undefined : resultsMarket,
        action: resultsAction === 'all' ? undefined : resultsAction,
        window_days: resultsWindow === 'all' ? undefined : resultsWindow,
        limit: 100,
      }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })

  const refreshMut = useMutation({
    mutationFn: () => api.backtestRefresh(),
    onSettled: () => {
      // Run takes seconds-to-minutes depending on suggestion volume;
      // poll a few seconds later so the operator sees the update.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['backtest'] })
      }, 5_000)
    },
  })

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Backtest</h1>
          <p className="text-sm text-muted-foreground">
            추천 엔진의 buy/sell 호출이 가격 움직임에 부합했는지 측정.
            매일 04:00 KST 에 자동, 또는 아래 버튼으로 즉시 실행. backtest.enabled=ON 후 동작.
          </p>
        </div>
        <Button
          size="sm"
          variant="primary"
          onClick={() => refreshMut.mutate()}
          disabled={refreshMut.isPending}
        >
          {refreshMut.isPending ? '실행 중…' : '지금 실행'}
        </Button>
      </header>

      {refreshMut.isError && (
        <Card className="border-destructive-soft bg-destructive-soft">
          <Card.Body className="text-sm text-destructive-soft-foreground">
            실행 트리거 실패: {String(refreshMut.error)}
          </Card.Body>
        </Card>
      )}
      {refreshMut.isSuccess && refreshMut.data && (
        <Card className="border-info-soft bg-info-soft">
          <Card.Body className="text-sm text-info-soft-foreground">
            실행 시작됨 — 후보 수에 따라 30초 ~ 수 분 후 갱신됩니다.
          </Card.Body>
        </Card>
      )}

      <SummaryCard
        loading={stats.isPending}
        totalResults={stats.data?.total_results ?? 0}
        statsRows={stats.data?.stats ?? []}
        period={period}
      />

      <Card>
        <Card.Header>
          <Card.Title>Rolling stats (hit-rate by 분류)</Card.Title>
        </Card.Header>
        <Card.Body className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Period (days)</label>
              <Select
                value={String(period)}
                onChange={(e) => setPeriod(Number(e.target.value) as Period)}
                className="w-28"
                options={[
                  { value: '7', label: '7일' },
                  { value: '30', label: '30일' },
                  { value: '90', label: '90일' },
                ]}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Market</label>
              <Select
                value={statsMarket}
                onChange={(e) => setStatsMarket(e.target.value as MarketFilter)}
                className="w-28"
                options={[
                  { value: 'all', label: '전체' },
                  { value: 'US', label: 'US' },
                  { value: 'KR', label: 'KR' },
                ]}
              />
            </div>
          </div>

          {stats.isPending && (
            <p className="text-sm text-muted-foreground">로딩 중…</p>
          )}
          {stats.error && (
            <p className="text-sm text-destructive-soft-foreground">
              로드 실패: {String(stats.error)}
            </p>
          )}
          {stats.data && stats.data.stats.length === 0 && (
            <EmptyState
              title="아직 통계가 없습니다"
              description={
                stats.data.total_results > 0
                  ? '선택한 period/market 에 데이터가 없습니다.'
                  : 'backtest.enabled=ON 또는 "지금 실행"으로 첫 통계를 만드세요. 추천이 충분히 쌓이고 (수일) overseas 스냅샷이 있어야 동작합니다.'
              }
            />
          )}
          {stats.data && stats.data.stats.length > 0 && (
            <StatsTable rows={stats.data.stats} />
          )}
        </Card.Body>
      </Card>

      {stats.data && stats.data.stats.length > 0 && (
        <Card>
          <Card.Header>
            <Card.Title>Hit-rate by sentiment band ({period}일)</Card.Title>
          </Card.Header>
          <Card.Body>
            <SentimentHistogram rows={stats.data.stats} />
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header>
          <Card.Title>Drilldown — 최근 추천의 hit/miss</Card.Title>
        </Card.Header>
        <Card.Body className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Market</label>
              <Select
                value={resultsMarket}
                onChange={(e) => setResultsMarket(e.target.value as MarketFilter)}
                className="w-28"
                options={[
                  { value: 'all', label: '전체' },
                  { value: 'US', label: 'US' },
                  { value: 'KR', label: 'KR' },
                ]}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Action</label>
              <Select
                value={resultsAction}
                onChange={(e) => setResultsAction(e.target.value as ActionFilter)}
                className="w-28"
                options={[
                  { value: 'all', label: '전체' },
                  { value: 'buy', label: 'Buy' },
                  { value: 'sell', label: 'Sell' },
                ]}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Window</label>
              <Select
                value={String(resultsWindow)}
                onChange={(e) =>
                  setResultsWindow(
                    e.target.value === 'all'
                      ? 'all'
                      : (Number(e.target.value) as 1 | 5 | 20),
                  )
                }
                className="w-28"
                options={[
                  { value: 'all', label: '전체' },
                  { value: '1', label: '1일' },
                  { value: '5', label: '5일' },
                  { value: '20', label: '20일' },
                ]}
              />
            </div>
          </div>

          {results.isPending && (
            <p className="text-sm text-muted-foreground">로딩 중…</p>
          )}
          {results.error && (
            <p className="text-sm text-destructive-soft-foreground">
              로드 실패: {String(results.error)}
            </p>
          )}
          {results.data && results.data.results.length === 0 && (
            <EmptyState
              title="결과가 없습니다"
              description="선택한 필터에 맞는 row 가 없습니다. 필터를 풀거나 '지금 실행'으로 갱신하세요."
            />
          )}
          {results.data && results.data.results.length > 0 && (
            <ResultsTable rows={results.data.results} />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ----- Summary card --------------------------------------------------------

function SummaryCard({
  loading,
  totalResults,
  statsRows,
  period,
}: {
  loading: boolean
  totalResults: number
  statsRows: BacktestStatsRow[]
  period: number
}) {
  // Aggregate top-line: total samples + overall hit_rate across the
  // visible statsRows. Weighted by sample_size so a single huge bucket
  // doesn't get drowned out by many tiny ones.
  let totalSamples = 0
  let totalHits = 0
  for (const r of statsRows) {
    totalSamples += r.sample_size
    totalHits += r.hit_count
  }
  const overallHitRate = totalSamples > 0 ? totalHits / totalSamples : null

  return (
    <Card>
      <Card.Header>
        <Card.Title>요약</Card.Title>
      </Card.Header>
      <Card.Body className="flex flex-wrap items-start gap-6">
        <div>
          <div className="text-xs text-muted-foreground">총 backtest_results 행</div>
          <div className="text-2xl font-semibold">
            {loading ? '…' : totalResults.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{period}일 표본 수</div>
          <div className="text-2xl font-semibold">
            {loading ? '…' : totalSamples.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{period}일 종합 hit-rate</div>
          <div className="text-2xl font-semibold">
            {loading
              ? '…'
              : overallHitRate === null
                ? '—'
                : `${(overallHitRate * 100).toFixed(0)}%`}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// ----- Stats table ---------------------------------------------------------

function StatsTable({ rows }: { rows: BacktestStatsRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Market</th>
            <th className="px-3 py-2 text-left font-medium">Action</th>
            <th className="px-3 py-2 text-left font-medium">Sentiment</th>
            <th className="px-3 py-2 text-left font-medium">Verdict</th>
            <th className="px-3 py-2 text-right font-medium">Window</th>
            <th className="px-3 py-2 text-right font-medium">n</th>
            <th className="px-3 py-2 text-right font-medium">Hit-rate</th>
            <th className="px-3 py-2 text-right font-medium">Avg return</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={`${r.period_days}-${r.market}-${r.action}-${r.sentiment_band}-${r.quality_verdict}-${r.window_days}`}
              className="border-b border-border last:border-0 hover:bg-muted/30"
            >
              <td className="px-3 py-2">
                <Badge variant={marketBadgeVariant(r.market)} shape="pill">
                  {r.market}
                </Badge>
              </td>
              <td className="px-3 py-2">
                <Badge variant={actionBadgeVariant(r.action)} shape="square">
                  {r.action}
                </Badge>
              </td>
              <td className="px-3 py-2 text-xs">{r.sentiment_band}</td>
              <td className="px-3 py-2 text-xs">{r.quality_verdict}</td>
              <td className="px-3 py-2 text-right text-xs">{r.window_days}d</td>
              <td className="px-3 py-2 text-right font-mono">{r.sample_size}</td>
              <td className="px-3 py-2 text-right font-mono">
                <HitRateCell hitRate={r.hit_rate} hitCount={r.hit_count} />
              </td>
              <td className="px-3 py-2 text-right font-mono">
                <AvgReturnCell avg={r.avg_return_pct} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function HitRateCell({
  hitRate,
  hitCount,
}: {
  hitRate?: number
  hitCount: number
}) {
  if (hitRate === undefined) return <span className="text-muted-foreground">—</span>
  const pct = hitRate * 100
  if (pct >= 60) return <span className="text-success">{pct.toFixed(0)}% ({hitCount})</span>
  if (pct < 40) return <span className="text-destructive">{pct.toFixed(0)}% ({hitCount})</span>
  return <span>{pct.toFixed(0)}% ({hitCount})</span>
}

function AvgReturnCell({ avg }: { avg?: number }) {
  if (avg === undefined) return <span className="text-muted-foreground">—</span>
  if (avg > 0) return <span className="text-success">+{avg.toFixed(2)}%</span>
  if (avg < 0) return <span className="text-destructive">{avg.toFixed(2)}%</span>
  return <span>{avg.toFixed(2)}%</span>
}

// ----- Sentiment histogram -------------------------------------------------

// Hand-rolled SVG bars (no chart dep — same pattern as Dashboard's
// SentimentSparkline). One bar per sentiment_band, height ∝ hit_rate
// weighted by sample_size across all (action, window, verdict) at that
// band. Colored by hit_rate (green ≥ 60%, red < 40%, neutral grey).
function SentimentHistogram({ rows }: { rows: BacktestStatsRow[] }) {
  // Bucket by sentiment_band → aggregate sample + hit count.
  const byBand: Record<string, { samples: number; hits: number }> = {}
  for (const r of rows) {
    if (!byBand[r.sentiment_band]) {
      byBand[r.sentiment_band] = { samples: 0, hits: 0 }
    }
    byBand[r.sentiment_band].samples += r.sample_size
    byBand[r.sentiment_band].hits += r.hit_count
  }
  const bandOrder = [
    'very_bearish',
    'bearish',
    'neutral',
    'bullish',
    'very_bullish',
    'unknown',
  ]
  const bars = bandOrder
    .map((band) => {
      const e = byBand[band]
      if (!e || e.samples === 0) return null
      return {
        band,
        samples: e.samples,
        rate: e.hits / e.samples,
      }
    })
    .filter(Boolean) as { band: string; samples: number; rate: number }[]

  if (bars.length === 0) {
    return <p className="text-sm text-muted-foreground">표시할 데이터가 없습니다.</p>
  }

  const w = 600
  const h = 160
  const barW = w / bars.length - 16
  const padTop = 20
  const padBottom = 36
  const innerH = h - padTop - padBottom

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="h-40 w-full"
      role="img"
      aria-label="Hit rate by sentiment band"
    >
      <line
        x1={0}
        x2={w}
        y1={padTop + innerH}
        y2={padTop + innerH}
        stroke="currentColor"
        strokeOpacity="0.2"
      />
      {bars.map((b, i) => {
        const x = i * (barW + 16) + 8
        const barH = b.rate * innerH
        const y = padTop + innerH - barH
        const pct = (b.rate * 100).toFixed(0)
        const fill =
          b.rate >= 0.6
            ? 'fill-success'
            : b.rate < 0.4
              ? 'fill-destructive'
              : 'fill-muted-foreground'
        return (
          <g key={b.band}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={barH}
              className={fill}
              opacity={0.7}
            />
            <text
              x={x + barW / 2}
              y={y - 4}
              textAnchor="middle"
              className="fill-foreground text-[10px]"
            >
              {pct}%
            </text>
            <text
              x={x + barW / 2}
              y={padTop + innerH + 14}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              {b.band}
            </text>
            <text
              x={x + barW / 2}
              y={padTop + innerH + 28}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              n={b.samples}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ----- Results (drilldown) table ------------------------------------------

function ResultsTable({ rows }: { rows: BacktestResultRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Ticker</th>
            <th className="px-3 py-2 text-left font-medium">Market</th>
            <th className="px-3 py-2 text-left font-medium">Action</th>
            <th className="px-3 py-2 text-right font-medium">Window</th>
            <th className="px-3 py-2 text-right font-medium">Entry</th>
            <th className="px-3 py-2 text-right font-medium">Exit</th>
            <th className="px-3 py-2 text-right font-medium">Return</th>
            <th className="px-3 py-2 text-center font-medium">Hit?</th>
            <th className="px-3 py-2 text-right font-medium">계산</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b border-border last:border-0 hover:bg-muted/30"
            >
              <td className="px-3 py-2 text-xs">
                <span className="font-mono">{r.ticker}</span>
                {symbolName(r.ticker) && (
                  <span className="ml-1.5 text-muted-foreground">
                    {symbolName(r.ticker)}
                  </span>
                )}
              </td>
              <td className="px-3 py-2">
                <Badge variant={marketBadgeVariant(r.market)} shape="pill">
                  {r.market}
                </Badge>
              </td>
              <td className="px-3 py-2">
                <Badge variant={actionBadgeVariant(r.action)} shape="square">
                  {r.action}
                </Badge>
              </td>
              <td className="px-3 py-2 text-right text-xs">{r.window_days}d</td>
              <td className="px-3 py-2 text-right font-mono">
                {r.entry_price !== undefined ? r.entry_price.toFixed(2) : '—'}
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {r.exit_price !== undefined ? r.exit_price.toFixed(2) : '—'}
              </td>
              <td className="px-3 py-2 text-right font-mono">
                <ReturnCell value={r.return_pct} />
              </td>
              <td className="px-3 py-2 text-center">
                <HitBadge hit={r.hit} />
              </td>
              <td
                className="px-3 py-2 text-right text-xs text-muted-foreground"
                title={formatTime(r.computed_at)}
              >
                {formatRelativeKST(r.computed_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ReturnCell({ value }: { value?: number }) {
  if (value === undefined) return <span className="text-muted-foreground">—</span>
  if (value > 0) return <span className="text-success">+{value.toFixed(2)}%</span>
  if (value < 0) return <span className="text-destructive">{value.toFixed(2)}%</span>
  return <span>{value.toFixed(2)}%</span>
}

function HitBadge({ hit }: { hit?: number }) {
  if (hit === undefined) {
    return (
      <Badge variant="muted" shape="pill" title="가격 데이터 부족 — 통계에서 제외">
        N/A
      </Badge>
    )
  }
  if (hit === 1) {
    return (
      <Badge variant="success" shape="pill">
        Hit
      </Badge>
    )
  }
  return (
    <Badge variant="warning" shape="pill">
      Miss
    </Badge>
  )
}

// ----- Helpers -------------------------------------------------------------

function marketBadgeVariant(
  market: string,
): 'success' | 'info' | 'warning' | 'muted' {
  switch (market) {
    case 'US':
    case 'NASDAQ':
      return 'success'
    case 'NYSE':
      return 'info'
    case 'KR':
    case 'KOSPI':
      return 'info'
    case 'AMEX':
      return 'warning'
    default:
      return 'muted'
  }
}

function actionBadgeVariant(
  action: string,
): 'success' | 'destructive' | 'muted' {
  switch (action) {
    case 'buy':
      return 'success'
    case 'sell':
      return 'destructive'
    default:
      return 'muted'
  }
}
